/** @jsx jsx */
// eslint-disable-next-line no-unused-vars
import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { injectTestId } from '@cvent/nucleus-test-automation';
import { ThemeContext } from '@cvent/carina-theme-provider';
import { jsx } from '@emotion/core';
import { getStyle } from './barGetStyle';

const barAPI = {
  /** the loading value for a definite loading bar. If left undefined, then the indefinite bar will be shown instead of a definite version. If a definite version is desired, then the way it works is that the value divided by the total (which has a default value of 100) and that will be the progress bar's progression */
  value: PropTypes.number,
  /** the largest number that the progress bar's  */
  total: PropTypes.number,
  /** the displayed text. Note: if you want to have the percentage displayed in text form, this is the place to do it, and it is up to you to provide the percentage as it changes */
  text: PropTypes.string,
  /** the size. Small is 100pixels wide to as wide as 150px, medium is between 200 and 250, and large is 300 to 350 */
  size: PropTypes.oneOf(['s', 'm', 'l']),
  /** the test ID, for testing */
  testID: PropTypes.string
};
export const ProgressBar = ({ value, text, size, testID, total, ...rest }) => {
  const theme = useContext(ThemeContext);
  const style = useStyle({ theme, size });
  const barStyle = () => {
    if (typeof value === 'undefined') {
      return {
        ...style.barProgressBar,
        ...style.indeterminate
      };
    }
    return {
      ...style.barProgressBar,
      ...style.determinate(Math.ceil((value / total) * 100))
    };
  };

  return (
    <div
      css={style.outerContainer}
      {...injectTestId(testID)}
      role="progressbar"
      aria-valuenow={value ? Math.ceil((value / total) * 100) : undefined}
      aria-live={typeof value !== 'undefined' ? 'assertive' : undefined}
    >
      {text && text}
      <div {...rest} css={style.barContainer}>
        <div css={barStyle()}></div>
      </div>
    </div>
  );
};

function useStyle({ size }) {
  const theme = useContext(ThemeContext);
  // const { prefersReducedMotion } = useContext(BreakpointsContext);
  const {
    barContainer,
    barProgressBar,
    indeterminate,
    outer
  } = React.useMemo(() => getStyle({ theme, size }), [theme, size]);
  return {
    outerContainer: { ...barContainer[size], ...outer },
    barContainer: { ...barContainer.base, ...barContainer[size] },
    barProgressBar: barProgressBar.base,
    indeterminate: indeterminate.base,

    determinate: value => {
      return {
        backgroundImage: `linear-gradient( to right, ${theme.backgroundColor.tertiary.fill.surface},  ${theme.backgroundColor.brand.fill.focus},   ${theme.backgroundColor.accent.fill.surface})`,
        clipPath: `inset( 0%  ${100 - value}%  0% 0% round  6px )`,
        // selector for edge and ie. Edge does not follow the -ms- vendor prefix,
        // but it also does not support clip path features, so in order to support edge, we need to lump it in with
        // the other microsoft browser. This is probably one of the least messy ways of going about it that I
        // could think up, but if there is another way that isn't a hack, please let me know.
        '_:-ms-lang(x), &': {
          width: `${value}%`
        },
        MsClipPath: `circle( ${100 - value}%  at 0% 50%)`
      };
    }
  };
}

ProgressBar.propTypes = barAPI;
ProgressBar.defaultProps = { total: 100 };
